/**
 * Versioned client storage with safe parsing and targeted invalidation.
 * Clears incompatible/corrupted state without wiping auth on every refresh.
 */

export const APP_STORAGE_VERSION = "2026-05-schema-6";

export const STORAGE_KEYS = {
    VERSION: "fairshare_app_storage_version",
    TOKEN: "fairshare_token",
    ACTIVE_ORG: "fairshare_active_org",
    ACTIVE_POOL: "fairshare_active_pool",
    BILLING_CACHE: "fairshare_billing_cache",
    APPROVALS_UI: "fairshare_approvals_ui",
    DASHBOARD_LAYOUT: "fairshare_dashboard_layout",
    RECOVERY_RELOAD: "fairshare_recovery_reload",
    LEFT_WORKSPACE: "fairshare_left_workspace",
};

/** Keys cleared on schema version bump (auth token preserved when still valid). */
const VOLATILE_KEYS = [
    STORAGE_KEYS.ACTIVE_ORG,
    STORAGE_KEYS.ACTIVE_POOL,
    STORAGE_KEYS.BILLING_CACHE,
    STORAGE_KEYS.APPROVALS_UI,
    STORAGE_KEYS.DASHBOARD_LAYOUT,
];

const LOG_PREFIX = "[FairShare]";

function logRecovery(message) {
    console.warn(`${LOG_PREFIX} ${message}`);
}

export function safeGetItem(key) {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

export function safeSetItem(key, value) {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
}

export function safeRemoveItem(key) {
    try {
        localStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

export function safeGetJSON(key, fallback = null) {
    const raw = safeGetItem(key);
    if (raw == null || raw === "") return fallback;
    try {
        return JSON.parse(raw);
    } catch {
        logRecovery(`Cleared corrupted cached client state (key: ${key})`);
        safeRemoveItem(key);
        return fallback;
    }
}

export function safeSetJSON(key, value) {
    try {
        safeSetItem(key, JSON.stringify(value));
        return true;
    } catch {
        return false;
    }
}

export function safeGetSessionItem(key) {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

export function safeSetSessionItem(key, value) {
    try {
        sessionStorage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
}

export function safeRemoveSessionItem(key) {
    try {
        sessionStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

export function clearVolatileAppState() {
    VOLATILE_KEYS.forEach((key) => safeRemoveItem(key));
}

export function clearAuthStorage() {
    safeRemoveItem(STORAGE_KEYS.TOKEN);
    safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
    clearVolatileAppState();
}

export function clearOrgScopedVolatileState() {
    safeRemoveItem(STORAGE_KEYS.ACTIVE_POOL);
    safeRemoveItem(STORAGE_KEYS.BILLING_CACHE);
    safeRemoveItem(STORAGE_KEYS.APPROVALS_UI);
    safeRemoveItem(STORAGE_KEYS.DASHBOARD_LAYOUT);
}

export function decodeJwtPayload(token) {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    try {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
        const json = atob(padded);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

export function isTokenShapeValid(token) {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload !== "object") return false;
    if (payload.type !== "access") return false;
    if (!payload.sub || typeof payload.sub !== "string") return false;
    if (payload.exp != null) {
        const expMs = Number(payload.exp) * 1000;
        if (!Number.isFinite(expMs) || expMs <= Date.now()) return false;
    }
    return true;
}

export function isValidUserShape(user) {
    return Boolean(
        user
        && typeof user === "object"
        && typeof user.id === "string"
        && user.id
        && typeof user.email === "string"
        && user.email,
    );
}

export function isValidOrgShape(org) {
    if (org == null) return true;
    return typeof org === "object" && typeof org.id === "string" && org.id.length > 0;
}

export function resolveStoredActiveOrgId(memberships = []) {
    const stored = safeGetItem(STORAGE_KEYS.ACTIVE_ORG);
    if (!stored) return null;
    const match = memberships.find((m) => m?.id === stored);
    if (!match) {
        safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
        return null;
    }
    return stored;
}

export function persistActiveOrgId(orgId) {
    if (orgId) safeSetItem(STORAGE_KEYS.ACTIVE_ORG, orgId);
    else safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
}

export function getStoredToken() {
    const token = safeGetItem(STORAGE_KEYS.TOKEN);
    if (!token) return null;
    if (!isTokenShapeValid(token)) {
        logRecovery("Cleared invalid auth token from storage");
        clearAuthStorage();
        return null;
    }
    return token;
}

export function persistToken(token) {
    if (!token) {
        safeRemoveItem(STORAGE_KEYS.TOKEN);
        return;
    }
    if (!isTokenShapeValid(token)) {
        logRecovery("Refused to persist malformed auth token");
        return;
    }
    safeSetItem(STORAGE_KEYS.TOKEN, token);
}

export function runClientStorageMigration() {
    const storedVersion = safeGetItem(STORAGE_KEYS.VERSION);
    if (storedVersion === APP_STORAGE_VERSION) {
        // Still validate token shape on every boot — cheap guard against corruption.
        getStoredToken();
        return { migrated: false };
    }

    logRecovery("Cleared incompatible cached client state");
    clearVolatileAppState();

    const token = safeGetItem(STORAGE_KEYS.TOKEN);
    if (token && !isTokenShapeValid(token)) {
        safeRemoveItem(STORAGE_KEYS.TOKEN);
    }

    safeSetItem(STORAGE_KEYS.VERSION, APP_STORAGE_VERSION);
    return { migrated: true, previousVersion: storedVersion };
}

export function markRecoveryReload() {
    safeSetSessionItem(STORAGE_KEYS.RECOVERY_RELOAD, "1");
}

export function consumeRecoveryReloadFlag() {
    const flag = safeGetSessionItem(STORAGE_KEYS.RECOVERY_RELOAD);
    if (flag) safeRemoveSessionItem(STORAGE_KEYS.RECOVERY_RELOAD);
    return flag === "1";
}

export function hasRecoveryReloadFlag() {
    return safeGetSessionItem(STORAGE_KEYS.RECOVERY_RELOAD) === "1";
}

export function markLeftWorkspace() {
    safeSetSessionItem(STORAGE_KEYS.LEFT_WORKSPACE, "1");
}

export function clearLeftWorkspaceFlag() {
    safeRemoveSessionItem(STORAGE_KEYS.LEFT_WORKSPACE);
}

export function hasLeftWorkspaceFlag() {
    return safeGetSessionItem(STORAGE_KEYS.LEFT_WORKSPACE) === "1";
}
